import { FC } from 'react';
import { Controller } from 'react-hook-form';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { DeleteIcon } from 'assets';
import { Rating } from '@mui/material';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import * as S from './styled';

export const FilledRectangleIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <rect
        width="10"
        height="20"
        x="7"
        y="2"
        fill="#85C553"
        stroke="transparent"
        strokeWidth="1"
        rx="2"
      />
    </SvgIcon>
  );
};

export const OutlinedRectangleIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <rect
        width="10"
        height="20"
        x="7"
        y="2"
        fill="#fff"
        stroke="#D8DBE8"
        strokeWidth="1"
        rx="2"
      />
    </SvgIcon>
  );
};

const MenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      marginTop: '2px',
      borderRadius: '8px',
      boxShadow: '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
      maxHeight: '245px',
      maxWidth: '440px',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: '4px' },
      '&::-webkit-scrollbar-thumb': {
        background: '#D8DBE8',
        borderRadius: '10px',
        transition: 'background 0.2s ease-in-out',
      },
      '&::-webkit-scrollbar-thumb:hover': { background: '#BFC3D4' },
      '&::-webkit-scrollbar-thumb:active': { background: '#A5AABF' },
    },
  },
  MenuListProps: {
    sx: {
      padding: '0px',
      borderRadius: '8px',
    },
  },
};

const MAX_SELECTION = 15;

const MultiplySelect: FC<any> = ({
  name,
  label,
  options = [],
  control,
  mode = '',
  rules = {},
  error = '',
  disabled = false,
  isShowNoneOption = false,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const selectedCount = field.value?.length || 0;
        const isMaxSelected = selectedCount >= MAX_SELECTION;

        const handleNoneSelection = () => {
          field.onChange([]);
        };

        // 🎯 Функция обновления `importance`, если передан `mode`
        const updateImportance = (id: number, newValue: number) => {
          if (!mode) return; // ✅ Работает ТОЛЬКО если `mode` передан

          const updatedValues = field.value.map((opt: any) => {
            return opt.id === id
              ? { ...opt, importance: newValue } // ✅ Обновляем ТОЛЬКО для выбранного `id`
              : opt;
          });

          field.onChange(updatedValues);
        };

        return (
          <div
            style={{
              marginBottom: 16,
              pointerEvents: disabled ? 'none' : 'auto',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <S.SelectLabel error={error}>{label}</S.SelectLabel>
            <S.CustomSelect
              multiple
              error={!!error}
              inputProps={{ 'aria-label': label }}
              fullWidth
              variant="outlined"
              IconComponent={KeyboardArrowDownIcon}
              displayEmpty
              MenuProps={MenuProps}
              value={
                field.value?.map((opt: any) => {
                  return opt.id;
                }) || []
              }
              onChange={(e: any) => {
                if (e.target.value.includes('')) {
                  field.onChange([]);
                  return;
                }
                const selectedIds: any = e.target.value.filter((id: any) => {
                  return id !== '';
                });
                const selectedObjects = options.filter((opt) => {
                  return selectedIds.includes(opt.id);
                });

                if (selectedObjects.length <= MAX_SELECTION) {
                  // ✅ Если mode передан – форматируем данные с importance
                  if (mode) {
                    const updatedObjects = selectedObjects.map((opt) => {
                      const existingItem = field.value?.find((val: any) => {
                        return val[mode].id === opt.id;
                      });

                      return {
                        id: opt.id, // ✅ Передаем id как отдельное поле
                        importance: existingItem?.importance ?? 1, // ✅ Сохраняем важность если уже была
                        [mode]: {
                          id: opt.id, // ✅ Внутри qualification тоже передаем id
                          name: opt.name, // ✅ Передаем name
                          group: opt.group || '',
                        },
                      };
                    });

                    field.onChange(updatedObjects);
                  } else {
                    // ✅ Если mode НЕ передан – просто сохраняем список объектов без importance
                    field.onChange(selectedObjects);
                  }
                }
              }}
              renderValue={(selected: any) => {
                if ((selected.length === 0 || selected.includes('')) && isShowNoneOption) {
                  return 'None';
                }

                const selectedNames = options
                  .filter((opt) => {
                    return selected.includes(opt.id);
                  })
                  .map((opt) => {
                    return opt.name;
                  })
                  .join(', ');
                return selectedNames || 'Select...';
              }}
            >
              <S.CustomMenuItem
                value=""
                onClick={handleNoneSelection}
                selected={field.value?.length === 0}
                disabled={!isShowNoneOption}
                isHide={!isShowNoneOption}
              >
                <span>None</span>
                {field.value?.length === 0 && (
                  <S.Checkmark>
                    <CheckIcon />
                  </S.Checkmark>
                )}
              </S.CustomMenuItem>
              {options.map((option: any) => {
                return (
                  <S.CustomMenuItem
                    key={option.id}
                    value={option.id}
                    disabled={
                      isMaxSelected &&
                      !field.value.some((val: any) => {
                        return val.id === option.id;
                      })
                    }
                  >
                    <span>{option.name}</span>
                    {field.value?.some((val: any) => {
                      return val.id === option.id;
                    }) && (
                      <S.Checkmark>
                        <CheckIcon />
                      </S.Checkmark>
                    )}
                  </S.CustomMenuItem>
                );
              })}
            </S.CustomSelect>

            {/* Отображение выбранных элементов с `importance`, если передан `mode` */}
            {field.value?.length > 0 &&
              (!mode ? (
                <S.SelectedOptionsContainer>
                  {field.value.map((option: any) => {
                    return (
                      <S.SelectedOption
                        key={option.id}
                        label={option.name || option?.skill?.name}
                        deleteIcon={
                          <S.DeleteIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              field.onChange(
                                field.value.filter((val: any) => {
                                  return val.id !== option.id;
                                })
                              );
                            }}
                          >
                            <DeleteIcon />
                          </S.DeleteIcon>
                        }
                        onDelete={() => {
                          return field.onChange(
                            field.value.filter((val: any) => {
                              return val.id !== option.id;
                            })
                          );
                        }}
                      />
                    );
                  })}
                </S.SelectedOptionsContainer>
              ) : (
                <S.SelectedOptionsImportanceContainer>
                  {field.value.map((option: any) => {
                    return (
                      <S.SelectedImportanceOption
                        key={option.id}
                        label={
                          <>
                            <Rating
                              name={`importance-${option.id}`}
                              value={option.importance || 1} // ✅ importance вместо rating
                              precision={1}
                              max={5}
                              onChange={(_, newValue) => {
                                return updateImportance(option.id, newValue || 1);
                              }}
                              icon={<FilledRectangleIcon />}
                              emptyIcon={<OutlinedRectangleIcon />}
                            />
                            <span
                              className="label"
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {option.name || option[mode].name}
                            </span>
                          </>
                        }
                        deleteIcon={
                          <S.DeleteIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              field.onChange(
                                field.value.filter((val: any) => {
                                  return val.id !== option.id;
                                })
                              );
                            }}
                          >
                            <DeleteIcon />
                          </S.DeleteIcon>
                        }
                        onDelete={() => {
                          return field.onChange(
                            field.value.filter((val: any) => {
                              return val.id !== option.id;
                            })
                          );
                        }}
                      />
                    );
                  })}
                </S.SelectedOptionsImportanceContainer>
              ))}
            {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
          </div>
        );
      }}
    />
  );
};

export default MultiplySelect;
